/** Account/profile endpoint calls — read, update, follow, followers/following, user posts. */
import type { AccountPrivate, AccountWithStats, FeedItem, PaginatedResponse, ReceivedClapsResponse, ActionParticipation } from '@olimhub/shared'

import type { Language } from '@olimhub/shared'
import { apiClient } from '../client'

export interface UpdateProfileInput {
  displayName?: string
  about?: string | null
  // Raw typed address. Empty/null clears the stored location (opt-out).
  // Geocoded server-side by default — unless latitude/longitude are also
  // sent, in which case those (from a Google Places pick) are trusted
  // directly, skipping the server's own geocode call.
  address?: string | null
  latitude?: number
  longitude?: number
  city?: string | null
  avatarUrl?: string
  links?: Array<{ label: string; url: string }>
  feedLanguages?: Language[]
}

export const accountsApi = {
  getMe: () =>
    apiClient.get<AccountPrivate>('/accounts/me').then((r) => r.data),

  // Self-only — see accounts.service.ts's getMyLocation comment. Powers
  // Search's merged map default center.
  getMyLocation: () =>
    apiClient.get<{ latitude: number; longitude: number } | null>('/accounts/me/location').then((r) => r.data),

  updateMe: (body: UpdateProfileInput) =>
    apiClient.put<AccountPrivate>('/accounts/me', body).then((r) => r.data),

  getByUsername: (username: string) =>
    apiClient.get<AccountWithStats>(`/accounts/${username}`).then((r) => r.data),

  getFollowers: (username: string, cursor?: string) =>
    apiClient
      .get<PaginatedResponse<AccountWithStats>>(`/accounts/${username}/followers`, { params: { cursor } })
      .then((r) => r.data),

  getFollowing: (username: string, cursor?: string) =>
    apiClient
      .get<PaginatedResponse<AccountWithStats>>(`/accounts/${username}/following`, { params: { cursor } })
      .then((r) => r.data),

  follow: (username: string) =>
    apiClient.post(`/accounts/${username}/follow`).then((r) => r.data),

  unfollow: (username: string) =>
    apiClient.delete(`/accounts/${username}/follow`).then((r) => r.data),

  report: (username: string, reason: string) =>
    apiClient.post(`/accounts/${username}/report`, { reason }).then((r) => r.data),

  getUserPosts: (username: string, params?: { cursor?: string; type?: string }) =>
    apiClient
      .get<PaginatedResponse<FeedItem>>(`/accounts/${username}/posts`, { params })
      .then((r) => r.data),

  getUserBoosts: (username: string, params?: { cursor?: string }) =>
    apiClient
      .get<PaginatedResponse<FeedItem>>(`/accounts/${username}/boosts`, { params })
      .then((r) => r.data),

  getParticipations: (username: string) =>
    apiClient.get<ActionParticipation[]>(`/accounts/${username}/participations`).then((r) => r.data),

  getSuggestions: () =>
    apiClient.get<AccountWithStats[]>('/accounts/suggestions').then((r) => r.data),

  getMyClaps: () =>
    apiClient.get<ReceivedClapsResponse>('/accounts/me/claps').then((r) => r.data),
}
